import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import './RegisterPage.css';
import logo from '../assets/logo-lnv.jpeg';

const RegisterPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [success, setSuccess] = useState(false);
    const location = useLocation();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validation
        if (!email || !password || !firstName || !lastName || !phoneNumber) {
            setError('Por favor, completa todos los campos obligatorios');
            return;
        }

        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        if (password.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres');
            return;
        }

        setLoading(true);

        try {
            console.log('[DEBUG] Attempting registration for:', email);
            await authService.register({
                email,
                password,
                firstName,
                lastName,
                phoneNumber: phoneNumber,
            });
            console.log('[DEBUG] Registration successful');

            // Set success state instead of redirecting
            setSuccess(true);
        } catch (err: any) {
            console.error('[DEBUG] Registration error:', err);
            const errorMessage = err.response?.data?.message || 'Error al registrar. El email puede estar en uso.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="register-page">
                <div className="register-container">
                    <div className="register-card" style={{ textAlign: 'center', padding: '3rem' }}>
                        <div className="register-header">
                            <div className="logo-container">
                                <img src={logo} alt="LNV Logo" className="logo-img" />
                            </div>
                        </div>
                        <div className="register-content">
                            <i className="fas fa-paper-plane" style={{ fontSize: '4rem', color: '#3b82f6', marginBottom: '1.5rem', display: 'block' }}></i>
                            <h2>¡Registro casi completo!</h2>
                            <p style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>
                                Hemos enviado un correo de verificación a <strong>{email}</strong>.<br />
                                Por favor, revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
                            </p>
                            <Link to="/login" state={{ from: (location.state as any)?.from }} className="btn btn-primary" style={{ display: 'inline-block', width: '100%', textDecoration: 'none', textAlign: 'center' }}>
                                Volver al Inicio de Sesión
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="register-page">
            <div className="register-container">
                <div className="register-card">
                    <div className="register-header">
                        <div className="logo-container">
                            <img src={logo} alt="LNV Logo" className="logo-img" />
                        </div>
                        <p className="register-subtitle">Crear Cuenta de Cliente</p>
                    </div>

                    <div className="register-content">
                        <h2>Regístrate</h2>
                        <p>Completa el formulario para crear tu cuenta</p>

                        <form className="register-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="firstName">Nombre *</label>
                                    <input
                                        id="firstName"
                                        type="text"
                                        placeholder="Tu nombre"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        disabled={loading}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="lastName">Apellido *</label>
                                    <input
                                        id="lastName"
                                        type="text"
                                        placeholder="Tu apellido"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        disabled={loading}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email *</label>
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="tu@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="phoneNumber">Teléfono *</label>
                                <input
                                    id="phoneNumber"
                                    type="tel"
                                    placeholder="+54 9 11 1234-5678"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="password">Contraseña *</label>
                                    <input
                                        id="password"
                                        type="password"
                                        placeholder="Mínimo 8 caracteres"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirmPassword">Confirmar *</label>
                                    <input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="Confirma tu contraseña"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        disabled={loading}
                                        required
                                    />
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <button
                                type="submit"
                                className="register-submit-btn"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="btn-loading">
                                        <div className="spinner"></div>
                                        <span>Registrando...</span>
                                    </div>
                                ) : (
                                    'Crear Cuenta'
                                )}
                            </button>
                        </form>

                        <div className="register-divider">
                            <span>¿Ya tienes cuenta?</span>
                        </div>

                        <Link to="/login" state={{ from: (location.state as any)?.from }} className="login-link">
                            Iniciar Sesión
                        </Link>
                    </div>

                    <div className="register-footer">
                        <p>&copy; 2026 LNV. Todos los derechos reservados.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
